import { useState, useEffect, useCallback, useMemo } from 'react';
import Router from 'next/router';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import getUuidByString from 'uuid-by-string';
import {
  TextField,
  FormControl,
  Button,
  Grid,
  CircularProgress,
  DialogActions,
  DialogContentText,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@material-ui/core';
import ApiService from '../../api/restApi';
import { useQalert, useSchema } from '../../utility/services/customHooks';
import { useCustomerInfo, useIsTestDriveUser, useTestDriveDomain } from '../../utility/services/customHooks/auth';
import { useDispatch, useSelector } from 'react-redux';
import { actionTypes } from '../../constants/actionTypes';
import { FormAutoComplete, FormDropDown } from '../formElements';
import fetchAndStoreCRData from '../../utility/services/indexedDB/content-recommendations/fetchAndStoreCRData';
import {
  getCRDetailPagePath,
  LOCAL_NEW_PAGE_DATA,
  getPageDraftDetailPagePath,
} from '../../utility/services/crListHelper';
import { format } from 'date-fns';
import { isEmpty, uniqBy } from 'lodash';
import { sortDomainOptions } from '../../utility/domainDropdownSort';
import { getHostNameList, validateUrlWithDomains } from '../../utility/validation/urls';
import { getCountryDetailsByAlpfa2, getCountryDetailsByAlpfa3 } from '../../constants/CountryAndLanguages';
import { COMPONENT_NAMES, SCREEN_NAMES, trackAnalyticsEvent } from '../../constants/analyticsMeta';
import { dispatchMasterDataQuery } from '../../utility/services/api/common';
import { ANNOTATION_SUB_TYPE_VALUE, ANNOTATION_TYPE } from '../../constants/formMeta/recommendation';
import { useUser } from '@auth0/nextjs-auth0/client';
import { _RESOLUTION_ENTITY_TYPE } from '../../utility/common';
import { typeAny } from '../../constants/typeDefinitions/commonType';
import ConfirmationModal from '../shared/modals/confirmationModal';
import WarningIcon from '@material-ui/icons/Warning';
import IfComponent from '../shared/IfComponent';
import { liveUrlValidator } from '../../utility/cr-common';

type propsType = {
  defaultPageUrl?: string;
  setShowCreateNewPagePopUp: (params: boolean) => void;
  discoverPagesModalSubmithandler: any;
  isFromDiscoverPagesModal?: boolean;
  domainsListFetched?: any;
  showSourceSelection?: boolean;
};

interface typeCountryData {
  location_code?: string;
  alpha_2?: string;
  alpha_3?: string;
  text?: string;
  value?: string;
  forTempSolu?: number;
}

const DEFAULT_COUNTRY: typeCountryData = {
  text: 'United States of America',
  value: 'United States of America',
  alpha_3: 'USA',
  alpha_2: 'US',
};
interface typeLanguageData {
  country_iso_code?: string;
  language_code?: string;
  // language_name?: string;
  location_code?: string | number | null;
  text?: string;
  value?: string;
  forTempSolu?: number;
}

const DEFAULT_LANGUAGE: typeLanguageData = {
  text: 'English',
  value: 'English',
  language_code: 'en',
  location_code: '2840',
  country_iso_code: 'US',
};

const AddPageToOptimize: React.FunctionComponent<propsType> = ({
  setShowCreateNewPagePopUp,
  defaultPageUrl = '',
  isFromDiscoverPagesModal = false,
  discoverPagesModalSubmithandler,
  domainsListFetched = [],
  showSourceSelection = false,
}) => {
  const [countryDataList, setCountryDataList] = useState<typeCountryData[]>([]);
  const [languageDataList, setLanguageDataList] = useState<typeLanguageData[]>([]);
  const [urlSlug, setUrlSlug] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<typeCountryData>({ ...DEFAULT_COUNTRY });
  const [selectedLanguage, setSelectedLanguage] = useState({ ...DEFAULT_LANGUAGE });
  const [customerDomain, setCustomerDomain] = useState<string>('');
  const [urlSlugError, setUrlSlugError] = useState('');
  const [selectedCountryError, setSelectedCountryError] = useState('');
  const [selectedLanguageError, setSelectedLanguageError] = useState('');
  const [existingPageData, setExistingPageData] = useState<object | null>(null);
  const schemaName = useSchema();

  // const [domainsList, setDomainsList] = useState<string[]>([]);
  const [domainAutocompleteList, setDomainAutocompleteList] = useState<{ text: string; name: string; value: string }[]>(
    []
  );
  const [selectedDomain, setSelectedDomain] = useState('');

  const [validationUrlMessage, setUrlValidationMessage] = useState('');
  const [selectedSource, setSelectedSource] = useState<string>('ORG');

  const [isApiInProcess, setIsApiInProcess] = useState(false);

  const [, toggleSnackBar] = useQalert();
  const dispatch = useDispatch();
  const masterLanguageAndCountry: { countryData: any[]; languageData: any[] } | object = useSelector(
    (state) => state.root.masterLanguageAndCountry
  );
  const customerInfo = useSelector((state) => state.root.customerInfo) || [];
  const { user } = useUser();
  const custDetails = useCustomerInfo(customerInfo);
  const isTestDriveUser = useIsTestDriveUser();
  const testDriveDomain = useTestDriveDomain();

  // New state for URL variation confirmation
  const [showUrlVariationConfirmation, setShowUrlVariationConfirmation] = useState(false);
  const [urlVariationData, setUrlVariationData] = useState<{
    originalUrl: string;
    foundVariation: string;
    variationType: string;
  } | null>(null);
  const [isGscCheckInProgress, setIsGscCheckInProgress] = useState(false);

  useEffect(() => {
    if (custDetails?.domain_url) {
      setCustomerDomain(new URL(custDetails?.domain_url)?.hostname);
    }
  }, [custDetails]);

  const isCrPaidPageWithGoogleAddSupport = useMemo(() => {
    return custDetails?.isGoogleAdsSupported;
  }, [custDetails?.isGoogleAdsSupported]);

  const cancelHandler = useCallback(() => {
    setShowCreateNewPagePopUp(false);
  }, [setShowCreateNewPagePopUp]);

  const findDuplicateUrl = useCallback(async (pageUrl) => {
    setIsApiInProcess(true);
    try {
      setExistingPageData(null);
      const checkUrlExistOrNot = await ApiService.post(
        `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/master-data/check-url-exist`,
        {
          pageUrl,
        }
      );
      if (checkUrlExistOrNot && checkUrlExistOrNot?.existingRecord) {
        setExistingPageData(checkUrlExistOrNot?.existingRecord);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('CreateNewPageFromExistingPage:findDuplicateUrl:: error', error);
      throw error;
    } finally {
      setIsApiInProcess(false);
    }
  }, []);

  const isAddPageForPaidSource = useMemo(() => {
    if (showSourceSelection) {
      return selectedSource === 'PAID';
    }
    const socure: string = (Router?.router?.query?.type || Router?.router?.query?.source || '') as string;
    return socure?.toLowerCase() === 'paid';
  }, [showSourceSelection, selectedSource]);

  const redirectHandler = useCallback((record, isForPaid = false) => {
    const source = isForPaid ? 'paid' : '';
    const isCnp = String(record?.is_cnp).toLowerCase() === 'true';
    const { path, params } = isCnp ? getPageDraftDetailPagePath(record) : getCRDetailPagePath(source, record);
    Router.push(path, params, { shallow: true });
  }, []);

  const existingUrlRedirectHandler = () => {
    redirectHandler(existingPageData);
  };

  const formatCountryForDropDown = useCallback(
    (list) => {
      const newList: typeCountryData[] = list.map((ele) => {
        return { text: ele.NAME, value: ele.NAME, alpha_3: ele.ALPHA_3, alpha_2: ele.ALPHA_2 };
      });
      setCountryDataList(newList);
      let defaultCountry = { ...DEFAULT_COUNTRY };
      if (custDetails?.primary_country) {
        const primaryCustomerCountry = getCountryDetailsByAlpfa3(custDetails?.primary_country);
        if (primaryCustomerCountry) {
          defaultCountry = {
            text: primaryCustomerCountry?.name,
            value: primaryCustomerCountry?.name,
            alpha_3: primaryCustomerCountry?.alpha_3,
            alpha_2: primaryCustomerCountry?.alpha_2,
          };
        }
      }
      setSelectedCountry({ ...defaultCountry });
    },
    [custDetails?.primary_country]
  );

  const formatLanguageForDropDown = useCallback((list) => {
    const newList: typeLanguageData[] = list.map((ele) => {
      return {
        text: ele.LANGUAGE_NAME,
        value: ele.LANGUAGE_NAME,
        language_code: ele.LANGUAGE_CODE,
        location_code: ele.LOCATION_CODE,
        country_iso_code: ele.COUNTRY_ISO_CODE,
      };
    });

    setLanguageDataList(newList);
    setSelectedLanguage({ ...DEFAULT_LANGUAGE });
  }, []);

  useEffect(() => {
    if (!isEmpty(masterLanguageAndCountry)) {
      if (masterLanguageAndCountry['countryData']) {
        formatCountryForDropDown(masterLanguageAndCountry['countryData']);
      }
      if (masterLanguageAndCountry['languageData']) {
        formatLanguageForDropDown(masterLanguageAndCountry['languageData']);
      }
    }
  }, [formatCountryForDropDown, formatLanguageForDropDown, masterLanguageAndCountry]);

  const autoSelectDomainAndSlugHandler = useCallback(
    (urlValue) => {
      try {
        const url = new URL(urlValue);
        const newOrigin = url.origin;
        let newPath = url.pathname + url.search + url.hash;
        newPath = newPath?.startsWith('/') ? newPath?.slice(1) : newPath;
        setUrlSlug(newPath);
        const findDomain = domainAutocompleteList?.find((domain) => domain?.value === newOrigin);
        if (findDomain) {
          setSelectedDomain(findDomain?.value);
        } else {
          setSelectedDomain('');
        }
      } catch (error) {
        const newUrl = urlValue?.startsWith('/') ? urlValue?.slice(1) : urlValue;
        setUrlSlug(newUrl);
      }
    },
    [domainAutocompleteList]
  );

  useEffect(() => {
    if (defaultPageUrl?.trim()) {
      autoSelectDomainAndSlugHandler(defaultPageUrl);
    }
  }, [defaultPageUrl, autoSelectDomainAndSlugHandler, domainAutocompleteList]);

  const onResetError = useCallback(() => {
    setUrlSlugError('');
    setExistingPageData(null);
    setUrlValidationMessage('');
    setSelectedCountryError('');
    setSelectedLanguageError('');
    setSkilValidationCheckForPaidScreen(false);
  }, []);

  const handleSlugPaste = useCallback(
    (e) => {
      onResetError();
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      autoSelectDomainAndSlugHandler(pastedText);
    },
    [onResetError, autoSelectDomainAndSlugHandler]
  );

  const [isDomainLoading, setIsDomainLoading] = useState(false);
  const fetchDomains = useCallback(
    async (
      domainUrl: string | undefined,
      testDriveDomain: string | undefined,
      isTestDriveUser: boolean | undefined
    ) => {
      try {
        setIsDomainLoading(true);
        setIsApiInProcess(true);
        const promiseArray = [
          dispatchMasterDataQuery(dispatch, ['domain', 'market_share_segment']),
          ApiService.get(`${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/settings/setup?type=domain`),
        ];
        const [masterDomainResponse, domainSetupResponse] = await Promise.all(promiseArray);
        const allDomains: string[] = [
          ...(getHostNameList(domainSetupResponse?.data?.domainSetup?.SUPPORTED_DOMAINS, 'origin') || ([] as string[])),
        ];
        const domainSetupTextValueMap =
          allDomains?.map((domain) => {
            return {
              value: new URL(domain)?.origin,
              name: domain?.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
              text: domain?.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
            };
          }) || [];

        const allAutoCompleteDomainList: { text: string; name: string; value: string }[] = [];
        if (masterDomainResponse?.domain?.length) {
          masterDomainResponse?.domain?.forEach((domain) => {
            allDomains.push(new URL(domain?.top_data)?.origin);
            allAutoCompleteDomainList.push({
              value: new URL(domain?.top_data)?.origin,
              name: domain?.top_data.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
              text: domain?.top_data.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
            });
          });
          setSelectedDomain(allAutoCompleteDomainList ? allAutoCompleteDomainList[0]?.value || '' : '');
        } else {
          if (custDetails?.domain_url) {
            const domainUrl = custDetails.domain_url;
            allDomains.push(new URL(domainUrl)?.origin);
            allAutoCompleteDomainList.push({
              value: new URL(domainUrl)?.origin,
              name: domainUrl.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
              text: domainUrl.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
            });
            setSelectedDomain(allAutoCompleteDomainList ? allAutoCompleteDomainList[0]?.value || '' : '');
          }
        }
        setDomainAutocompleteList([...allAutoCompleteDomainList, ...domainSetupTextValueMap]);
      } catch (error) {
        console.error('AddPageToOptimize:: fetchDomains::error::', error);
      } finally {
        setIsApiInProcess(false);
        setIsDomainLoading(false);
      }
    },
    [custDetails?.domain_url, dispatch]
  );

  const validateForm = useCallback(
    (customUrl: string | null = null) => {
      // Skip form validation if using custom URL (GSC URL)
      if (customUrl) {
        return true;
      }

      let isFormValid = true;
      // Reset errors
      setUrlSlugError('');
      setUrlValidationMessage('');
      setSelectedCountryError('');
      setSelectedLanguageError('');
      // Validate domain and URL slug
      if (!selectedDomain) {
        isFormValid = false;
        setUrlValidationMessage('Please select a domain');
      }
      // Validate URL slug
      if (urlSlug.startsWith('/')) {
        isFormValid = false;
        setUrlSlugError('Please remove / from the beginning of URL slug');
      }
      if (/['"]/.test(urlSlug)) {
        isFormValid = false;
        setUrlSlugError('URL slug cannot contain quotes. Please remove them and try again.');
      }
      // Construct full URL for validation
      const fullUrl = selectedDomain + '/' + urlSlug.replace(/^\/+/, '');
      // Validate full URL against domains
      // const allDomains = customerDomain ? [...domainsListFetched, customerDomain] : domainsListFetched;
      const allDomains = getHostNameList(domainAutocompleteList?.map((item) => item?.value) as typeAny) || [];
      const { isValidUrl, errorMessage } = validateUrlWithDomains(fullUrl, [...allDomains, customerDomain], true);
      if (!isValidUrl) {
        isFormValid = false;
        setUrlValidationMessage(errorMessage);
      }
      // Country validation
      if (!selectedCountry?.value || !selectedCountry?.alpha_3) {
        isFormValid = false;
        setSelectedCountryError('Please select country');
      }
      // Language validation
      if (!selectedLanguage?.language_code || !selectedLanguage?.location_code || !selectedLanguage?.value) {
        isFormValid = false;
        setSelectedLanguageError('Please select language');
      }
      return isFormValid;
    },
    [
      selectedDomain,
      urlSlug,
      domainAutocompleteList,
      customerDomain,
      selectedCountry?.value,
      selectedCountry?.alpha_3,
      selectedLanguage?.language_code,
      selectedLanguage?.location_code,
      selectedLanguage?.value,
    ]
  );

  // New function to check URL variations in GSC
  const checkUrlVariationInGSC = useCallback(async (fullUrl: string) => {
    try {
      setIsGscCheckInProgress(true);

      const gscCheckResponse = await ApiService.post(
        `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/content-recommendations/check-url-variation`,
        {
          pageUrl: fullUrl,
        }
      );

      if (gscCheckResponse?.data && gscCheckResponse?.data?.foundGSC && gscCheckResponse?.data?.urlData?.length > 0) {
        // Check if any URL in GSC has only trailing slash difference
        const urlsWithTrailingSlashDifference = gscCheckResponse.data.urlData.filter((gscUrl: string) => {
          const userUrlNormalized = fullUrl.endsWith('/') ? fullUrl.slice(0, -1) : fullUrl;
          const gscUrlNormalized = gscUrl.endsWith('/') ? gscUrl.slice(0, -1) : gscUrl;
          // Same URL but different trailing slash
          return userUrlNormalized === gscUrlNormalized && fullUrl !== gscUrl;
        });

        if (urlsWithTrailingSlashDifference.length > 0) {
          // Found URL with trailing slash difference - show confirmation
          const foundVariation = urlsWithTrailingSlashDifference[0];
          const variationType = foundVariation.endsWith('/') ? 'with trailing slash' : 'without trailing slash';

          setUrlVariationData({
            originalUrl: fullUrl,
            foundVariation: foundVariation,
            variationType: variationType,
          });
          return true; // Show confirmation popup
        }
      }

      // No trailing slash differences found - proceed without confirmation
      return false;
    } catch (error) {
      console.error('checkUrlVariationInGSC error:', error);
      return false; // On error, proceed normally
    } finally {
      setIsGscCheckInProgress(false);
    }
  }, []);

  const [skipValidationCheckForPaidScreen, setSkilValidationCheckForPaidScreen] = useState(false);

  // Modified onSubmitHandler to include GSC variation check
  const onSubmitHandler = useCallback(
    async (skipGscCheck = false, customUrl: string | null = null) => {
      const isFormValid = validateForm(customUrl);

      if (!isFormValid || !custDetails) return;

      try {
        // Use custom URL if provided, otherwise construct from form
        const fullUrl = customUrl || selectedDomain + '/' + urlSlug.replace(/^\/+/, '');
        const [existingRecord, foundVariation] = await Promise.all([
          findDuplicateUrl(fullUrl),
          !skipGscCheck &&
          !isFromDiscoverPagesModal &&
          !customUrl &&
          (isAddPageForPaidSource ? !skipValidationCheckForPaidScreen : true)
            ? checkUrlVariationInGSC(fullUrl)
            : null,
        ]);
        if (existingRecord) return;

        if (foundVariation) {
          setShowUrlVariationConfirmation(true);
          return; // Stop here and wait for user confirmation
        }

        let dataSource = isAddPageForPaidSource ? 'PAID' : 'ORG';
        if (isAddPageForPaidSource && !skipValidationCheckForPaidScreen) {
          console.log('PaidPage:: validation check start');
          setIsApiInProcess(true);
          const [isLivePage, pageSource] = await Promise.all([
            liveUrlValidator(fullUrl),
            ApiService.post(`${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/page-optimization/get-page-source`, {
              pageUrl: fullUrl,
            }),
          ]);
          dataSource = pageSource?.data || 'PAID';
          if (dataSource !== 'PAID' && !isLivePage) {
            toggleSnackBar({ type: 'error', text: 'The page is not currently live.', open: true });
            return;
          } else if (dataSource !== 'PAID' && isLivePage) {
            setSkilValidationCheckForPaidScreen(true);
            return; ///trerminate code;
          }
          setIsApiInProcess(false);
          dataSource = dataSource === 'ORG' ? 'ORG & PAID' : dataSource;
        } else if (isAddPageForPaidSource) {
          console.log('PaidPage:: validation check start');
        }
        // Check for URL variations in GSC if not skipping
        // if (!skipGscCheck && !isFromDiscoverPagesModal && !customUrl) {
        //   const foundVariation = await checkUrlVariationInGSC(fullUrl);
        //   if (foundVariation) {
        //     setShowUrlVariationConfirmation(true);
        //     return; // Stop here and wait for user confirmation
        //   }
        // }

        setIsApiInProcess(true);
        const locationCodeBasedOnCountry = getCountryDetailsByAlpfa2(selectedCountry?.alpha_2);
        const additional_page_info = {
          country: { ...selectedCountry, location_code: locationCodeBasedOnCountry?.location_code },
          language: { ...selectedLanguage, location_code: locationCodeBasedOnCountry?.location_code || null },
        };

        const payload = {
          content_recommendation_id: getUuidByString(`${custDetails.cust_code}_${fullUrl}`),
          page: fullUrl,
          data_source: dataSource || 'ORG',
          additional_page_info,
        };

        const [response] = await Promise.all([
          ApiService.post(
            `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/content-recommendations/create-page`,
            payload
          ),
        ]);
        setSkilValidationCheckForPaidScreen(false); ///reset
        if (response) {
          const dateValue = new Date();
          const annotationPayload = {
            recommendation_id: payload.content_recommendation_id,
            reason: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS,
            date: format(dateValue, 'MM/dd/yyyy'),
            entity_type: _RESOLUTION_ENTITY_TYPE.CONTENT_RECOMMENDATIONS,
            entity_id: payload.content_recommendation_id,
            annotation_type: ANNOTATION_TYPE.RESOLUTION,
            annotation_subtype: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS,
            annotation_details: {},
          };

          let pageStatusChangeResponse = {};
          try {
            const result = await ApiService.post(
              `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/utility/annotation`,
              annotationPayload
            );
            pageStatusChangeResponse = { ...(result?.data || {}), reason: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS };
          } catch (error) {
            console.error('AddPageToOptimize :: changeStatusForPageHandler:: error', error);
          }

          trackAnalyticsEvent({
            eventFor: 'button',
            buttonText: 'form submit',
            counterName: 'Pages Added to Optimize',
            isAddCount: true,
            user,
            payload: {
              label: 'Add New Page',
              customer_url: fullUrl,
              component_name: COMPONENT_NAMES.content_recommendations,
              screen_name: SCREEN_NAMES.content_recommendations,
              dataSource,
            },
          });

          const newPageRecord = {
            ...LOCAL_NEW_PAGE_DATA,
            ...pageStatusChangeResponse,
            uuid: payload.content_recommendation_id,
            data_source: payload.data_source,
            finalurl: payload.page,
            site: custDetails.domain_url as string,
            created_date: format(dateValue, 'MM/dd/yyyy'),
          };

          dispatch({ type: actionTypes.SET_NEW_PAGE_CONTENT_RECOMMENDATION_DATA, payload: newPageRecord });
          fetchAndStoreCRData([newPageRecord], schemaName, false, fullUrl);
          dispatch({ type: actionTypes.ADD_NEW_PAGE_IN_CR_FILTERS, page: fullUrl });
          dispatch({ type: actionTypes.ADD_PAGE_TO_OPTIMIZE_FORM_BOX, page: {} });
          redirectHandler(newPageRecord, isAddPageForPaidSource);
        }
      } catch (e) {
        console.error('Add new page :: Api Error :: ', e);
        toggleSnackBar({ type: 'error', text: 'Oops! Something Went Wrong. Please try again.', open: true });
      } finally {
        setIsApiInProcess(false);
      }
    },
    [
      validateForm,
      custDetails,
      selectedDomain,
      urlSlug,
      findDuplicateUrl,
      isAddPageForPaidSource,
      skipValidationCheckForPaidScreen,
      isFromDiscoverPagesModal,
      selectedCountry,
      selectedLanguage,
      toggleSnackBar,
      checkUrlVariationInGSC,
      user,
      dispatch,
      schemaName,
      redirectHandler,
    ]
  );

  // Handler for URL variation confirmation
  const handleUrlVariationConfirmation = useCallback(() => {
    setShowUrlVariationConfirmation(false);
    setUrlVariationData(null);
    // Proceed with form submission, skipping GSC check (Add as separate page)
    onSubmitHandler(true);
  }, [onSubmitHandler]);

  const handleUrlVariationCancel = useCallback(() => {
    setShowUrlVariationConfirmation(false);
    setUrlVariationData(null);
  }, []);

  // New handler for replacing GSC URL
  const handleReplaceGscUrl = useCallback(() => {
    setShowUrlVariationConfirmation(false);
    // Use the GSC URL instead of user's URL by passing it as customUrl parameter
    if (urlVariationData?.foundVariation) {
      onSubmitHandler(true, urlVariationData.foundVariation);
    }
    setUrlVariationData(null);
  }, [urlVariationData, onSubmitHandler]);

  const uniqueDomainDropDownList = useMemo(() => {
    // Display-only ordering: dedupe first so the existing "first entry wins" rule still
    // picks the same option, then sort purely for the dropdown. selectedDomain is resolved
    // in fetchDomains from the unsorted source, so the pre-selected domain is unchanged.
    return sortDomainOptions(uniqBy(domainAutocompleteList, 'text'));
  }, [domainAutocompleteList]);

  const uniqueLanguageDataList = useMemo(() => {
    return uniqBy(languageDataList, 'text') || [];
  }, [languageDataList]);

  const uniqueCountryDataList = useMemo(() => {
    return uniqBy(countryDataList, 'alpha_2') || [];
  }, [countryDataList]);

  useEffect(() => {
    fetchDomains(custDetails?.domain_url, testDriveDomain as string | undefined, isTestDriveUser);
  }, [custDetails?.domain_url, fetchDomains, isTestDriveUser, masterLanguageAndCountry, testDriveDomain]);

  const domainSetupRedirectHandler = () => {
    Router.push('/app/settings/setup/add-domains');
  };

  // /////---------------------RETURN COMPONENT---------------------///////
  return (
    <>
      <Dialog
        PaperProps={{
          style: {
            width: 500,
            maxWidth: 500,
            borderRadius: '4px',
          },
        }}
        onClose={() => {}}
        aria-labelledby="simple-dialog-title"
        maxWidth="sm"
        open={true}
        data-testid="addNewKeywordDlgBoxWrapper"
        className="add-page-optimize-wrapper"
      >
        {
          <DialogTitle
            className="new-keyword-dialog-title"
            style={{ padding: '10px 25px 0px 25px' }}
            data-testid="addNewKeywordDlgTitle"
          >
            {isFromDiscoverPagesModal ? 'Add Page To Optimize' : 'Add New Page'}
          </DialogTitle>
        }
        <DialogContent className={''} style={{ marginTop: '20px' }} data-testid="addNewKeywordDlgContent">
          <div className="create-new-page-popup">
            {!isFromDiscoverPagesModal ? (
              <Grid container style={{ alignItems: 'center', justifyContent: 'start' }} className="mb-12">
                <Grid item xs={6} sm={5} style={{ display: 'flex' }}>
                  <FormDropDown
                    className={`w-100 ${isDomainLoading ? 'text-box-loader' : ''}`}
                    fullWidth
                    fullHeight
                    label="Select Domain"
                    options={uniqueDomainDropDownList}
                    searchable
                    searchPlaceholder="Search domain"
                    noSearchResultsText="No matching domain"
                    onChange={(res) => {
                      setSelectedDomain(res?.target?.value || '');
                      onResetError();
                    }}
                    value={selectedDomain || ''}
                    defaultValue={selectedDomain || ''}
                    disabled={isDomainLoading}
                  />
                  <p style={{ marginLeft: '5px', marginRight: '5px', paddingTop: '5px' }}> / </p>
                </Grid>
                <Grid item xs={6} sm={7}>
                  <TextField
                    label="URL Slug"
                    value={urlSlug}
                    onPaste={handleSlugPaste}
                    className={`w-100 ${isDomainLoading ? 'text-box-loader' : ''}`}
                    onChange={(event) => {
                      setUrlSlug(event.target.value);
                      onResetError();
                    }}
                    error={urlSlugError || existingPageData ? true : false}
                    inputProps={{
                      style: {
                        textTransform: 'lowercase',
                        borderColor: urlSlugError || existingPageData ? 'red' : '',
                      },
                    }}
                    fullWidth
                    variant="outlined"
                    name="url_slug"
                    disabled={isDomainLoading}
                  />
                </Grid>
              </Grid>
            ) : null}
            {validationUrlMessage?.trim() ? (
              <p className="m-0 mt-5 new-page-form-error">{validationUrlMessage}</p>
            ) : null}
            {urlSlugError ? <p className="m-0 mt-5 new-page-form-error">{urlSlugError}</p> : null}
            {existingPageData ? (
              <p className="m-0 url-exist-message">
                Page already exists.
                {/* eslint-disable-next-line */}
                <span className="redirect-click-btn" onClick={existingUrlRedirectHandler}>
                  {' '}
                  Click here{' '}
                </span>
                to view.
              </p>
            ) : null}
            {!(urlSlugError || existingPageData) && !isFromDiscoverPagesModal ? (
              <p className=" mt-10 missing-domain-note">
                Don't see your domain? Please add your domain in the {/* eslint-disable-next-line */}
                <span className="click-here" onClick={domainSetupRedirectHandler}>
                  domain setup
                </span>
                .
              </p>
            ) : null}
            <IfComponent isValid={showSourceSelection && isCrPaidPageWithGoogleAddSupport}>
              <Grid
                container
                style={{ alignItems: 'center', justifyContent: 'start' }}
                className="mt-15 mb-12"
                data-testid="source-selection"
              >
                <FormControl component="fieldset" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FormLabel component="legend" style={{ marginRight: '16px', color: 'rgba(0, 0, 0, 0.54)' }}>
                      Traffic Source:
                    </FormLabel>
                    <RadioGroup
                      row
                      value={selectedSource}
                      onChange={(e) => {
                        onResetError();
                        setSelectedSource(e.target.value);
                      }}
                      style={{ flexDirection: 'row' }}
                    >
                      <FormControlLabel value="ORG" control={<Radio color="primary" />} label="Organic" />
                      <FormControlLabel value="PAID" control={<Radio color="primary" />} label="Paid" />
                    </RadioGroup>
                  </div>
                </FormControl>
              </Grid>
            </IfComponent>
            <Grid
              container
              style={{ alignItems: 'center', justifyContent: 'start' }}
              className="mt-15 mb-12"
              data-testid="country-selection"
            >
              <FormControl variant="outlined" style={{ width: '100%' }} data-testid="select-country-test">
                <FormAutoComplete
                  value={selectedCountry}
                  options={uniqueCountryDataList}
                  label={'Select Country'}
                  className={'mb-25 w-100'}
                  onChange={(value) => {
                    setSelectedCountry(value);
                  }}
                  onTextBlur={(value) => {
                    const findCountry: typeCountryData = uniqueCountryDataList?.find(
                      (country) => country?.text === value || country?.value === value
                    ) || {
                      forTempSolu: new Date().getTime(),
                    };
                    setSelectedCountry(findCountry);
                  }}
                  fullWidth
                  disableClearable
                />
              </FormControl>
              <p className="m-0 mt-5 new-page-form-error">{selectedCountryError}</p>
            </Grid>
            <Grid
              container
              style={{ alignItems: 'center', justifyContent: 'start' }}
              className="mt-15 mb-12"
              data-testid="language-selection"
            >
              <FormControl variant="outlined" style={{ width: '100%' }}>
                <FormAutoComplete
                  value={selectedLanguage}
                  options={uniqueLanguageDataList}
                  label={'Select Language'}
                  className={'mb-25 w-100'}
                  onChange={(value) => {
                    setSelectedLanguage(value);
                  }}
                  onTextBlur={(value) => {
                    const findLang: typeLanguageData = uniqueLanguageDataList?.find(
                      (lang) => lang?.text === value || lang?.value === value
                    ) || {
                      forTempSolu: new Date().getTime(),
                    };

                    setSelectedLanguage(findLang);
                  }}
                  fullWidth
                  disableClearable
                />
              </FormControl>
              <p className="m-0 mt-5 new-page-form-error">{selectedLanguageError}</p>
            </Grid>
            <IfComponent isValid={skipValidationCheckForPaidScreen}>
              <p className="m-0 new-page-form-error">
                This URL has no paid impressions in the past 3 months. Continue with URL as entered?
              </p>
            </IfComponent>
            <Grid container style={{ alignItems: 'center', justifyContent: 'start' }} className="mt-15 mb-12">
              <Grid item xs={12} sm={12} className="align-self-center">
                <div style={{ float: 'right' }}>
                  <span style={{ marginRight: '8px' }}>
                    <Button
                      className={`create-keyword-cancel-btn`}
                      variant="outlined"
                      color="primary"
                      onClick={cancelHandler}
                      style={{ marginTop: '8px' }}
                      data-testid="cancelBtn"
                    >
                      Cancel
                    </Button>
                    <Button
                      className={`create-intent-cta`}
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        isFromDiscoverPagesModal
                          ? discoverPagesModalSubmithandler(selectedCountry, selectedLanguage)
                          : onSubmitHandler();
                      }}
                      style={{ marginTop: '8px', textTransform: 'capitalize', boxShadow: 'none' }}
                      data-testid="addpageBtn"
                      disabled={isApiInProcess || !selectedDomain || isGscCheckInProgress}
                    >
                      {isApiInProcess || isGscCheckInProgress ? (
                        <CircularProgress className="position-abs" size={20} />
                      ) : null}
                      {skipValidationCheckForPaidScreen
                        ? 'Continue'
                        : isFromDiscoverPagesModal
                        ? 'Add Page To Optimize'
                        : 'Add New Page'}
                    </Button>
                  </span>
                </div>
              </Grid>
            </Grid>
          </div>
        </DialogContent>
      </Dialog>

      {/* URL Variation Confirmation Modal */}
      {showUrlVariationConfirmation && urlVariationData && (
        <Dialog
          open={showUrlVariationConfirmation}
          onClose={() => {}}
          maxWidth="sm"
          aria-labelledby="url-variation-dialog-title"
        >
          <DialogTitle style={{ color: '000000', paddingBottom: '8px' }}>
            <WarningIcon style={{ color: 'orange', fontSize: 26, margin: '0px 8px -5px 0px' }} />
            URL Variation Found in Google Search Console (GSC)
          </DialogTitle>
          <DialogContent>
            <DialogContentText style={{ color: '#000000', fontSize: '16px', marginBottom: '16px' }}>
              We found a similar URL in GSC that differs only by a trailing slash.
            </DialogContentText>

            <DialogContentText style={{ color: '#000000', fontWeight: '500', fontSize: '15px', marginBottom: '8px' }}>
              Your Entered URL:
              <div style={{ fontWeight: '400', fontSize: '15px', marginBottom: '12px', wordBreak: 'break-all' }}>
                {urlVariationData.originalUrl}
              </div>
            </DialogContentText>

            <DialogContentText style={{ color: '#000000', fontWeight: '500', fontSize: '15px', marginBottom: '8px' }}>
              Already in GSC:
              <div style={{ fontWeight: '400', fontSize: '15px', marginBottom: '12px', wordBreak: 'break-all' }}>
                {urlVariationData.foundVariation}
              </div>
            </DialogContentText>

            <DialogContentText style={{ color: '#000000', fontWeight: '500', fontSize: '15px', marginBottom: '8px' }}>
              ✅ Both URLs point to the same page. For SEO, it’s best to pick one version as the primary (canonical)
              URL.
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '16px 24px 24px 24px', justifyContent: 'space-between' }}>
            <Button
              onClick={handleUrlVariationCancel}
              className={`create-keyword-cancel-btn`}
              variant="outlined"
              color="primary"
              disabled={isApiInProcess}
            >
              Cancel
            </Button>
            <div>
              <Button
                className={`create-keyword-cancel-btn`}
                onClick={handleUrlVariationConfirmation}
                variant="outlined"
                color="primary"
                style={{ marginRight: '12px' }}
                disabled={isApiInProcess}
              >
                {isApiInProcess ? <CircularProgress size={20} /> : null}
                Use Current URL
              </Button>
              <Button
                className={`create-intent-cta`}
                onClick={handleReplaceGscUrl}
                variant="contained"
                color="primary"
                style={{ textTransform: 'capitalize', background: '#1D3557' }}
                disabled={isApiInProcess}
              >
                {isApiInProcess ? <CircularProgress size={20} /> : null}
                Use GSC URL
              </Button>
            </div>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default AddPageToOptimize;
